import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import './PostCard.scss';

import { ROUTER_ENDPOINTS } from '../../../router/router-endpoints';
import networker from '../../../services/networking';
import { useAuth } from '../../../services/auth/auth-context';

import { CardCreateData } from '../shared/CardCreateData/CardCreateData';
import { PostText } from './PostText/PostText';
import { CardStats } from '../shared/CardStats/CardStats';
import { CardButtons } from '../shared/CardButtons/CardButtons';
import { PostResponse } from '../../../types/models/posts';
import { IPostReactionCreateData, PostReactionResponse, ReactionType } from '../../../types/models/reaction';
import { doesArrayHaveObjectWithProp } from '../../../utils/arrays';

interface IPostCardProps {
  post: PostResponse;
  showCopyLinkButton?: boolean;
  isDemo?: boolean;
  isLanding?: boolean;
  isShowcase?: boolean;
}

export function PostCard({ post, showCopyLinkButton, isDemo, isLanding, isShowcase = false }: IPostCardProps) {
  const [displayPost, setDisplayPost] = useState(post);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isLikePressed, setIsLikePressed] = useState(false);
  const [isDislikePressed, setIsDislikePressed] = useState(false);
  const history = useHistory();
  const auth = useAuth();
  
  // if like/dislike from current user => it is pressed and inactive.
  // if like/dislike from current user => dislike/like is dissabled.
  useEffect(() => {
    if (!auth.user) return;
    setIsLiked(
      doesArrayHaveObjectWithProp({
        array: displayPost.likes,
        propName: 'authorUid',
        value: auth.user?.uid
      })
    );
    setIsDisliked(doesArrayHaveObjectWithProp({
      array: displayPost.dislikes,
      propName: 'authorUid',
      value: auth.user?.uid
    }));
  }, [displayPost.likes.length, displayPost.dislikes.length]);

  // TODO: Maybe create a generic "createReaction" for CommentCard and PostCard. They share the same action. Differences are only in state vars and networker methods.
  function createReaction(type: ReactionType) {
    if (!auth.user) {
      return null;
    }
    const reactionData: IPostReactionCreateData = {
      postUid: displayPost.body.uid,
      type,
      authorUid: auth.user?.uid,
    };
    networker.createReactionPost(reactionData)
      .then((res: PostReactionResponse | undefined) => {
        if (!res) {
          return null;
        }
        // press the button:
        const buttonStateFunc = type === 'LIKE' ? setIsLikePressed : setIsDislikePressed;
        buttonStateFunc(true); 
        // add new reaction to the stats and post object:
        const statsProp = type === 'LIKE' ? 'likes' : 'dislikes';
        displayPost.stats[statsProp]++;
        displayPost[statsProp].push(res);
        setDisplayPost({ ...displayPost });
      });   
  }

  const handler = isDemo
    ? {
        details() {
          // history.push(ENDPOINTS.POST_DETAILS);
        },
        like() {
          // TODO: increase likes
        } ,
        dislike() {
          // TODO: increase dislikes
        },
        profile() {
          // TODO: make unclickable
        }
      }
    : isLanding
      ? {
          details() {
            history.push(`${ROUTER_ENDPOINTS.POST_DETAILS}/${post.body.uid}`);
          },
          like() {
            history.push(ROUTER_ENDPOINTS.AUTH);
          } ,
          dislike() {
            history.push(ROUTER_ENDPOINTS.AUTH);
          },
          profile() {
            history.push(`${ROUTER_ENDPOINTS.PROFILE}/${post.body.authorUid}`);
          }
        }
      : {
          details() {
            history.push(`${ROUTER_ENDPOINTS.POST_DETAILS}/${post.body.uid}`);
          },
          like() {
            createReaction('LIKE'); 
          },
          dislike() {
            createReaction('DISLIKE');
          },
          profile() {
            history.push(`${ROUTER_ENDPOINTS.PROFILE}/${post.body.authorUid}`);
          }
        };

    const cssClassPostCardLi = `
      PostCard__li
      ${
        isShowcase
          ? ` PostCard__li--ignore-width`
          : ''
      }
    `;

  return (
    <li key={ displayPost.body.uid } className={ cssClassPostCardLi }>
      <div className='PostCard'>

        <CardCreateData
          author={ displayPost.body.author }
          postCreated={ displayPost.body.createdAt }
          showHints={true}
          onUsernameClick={handler.profile}
        />

        <PostText
          text={ displayPost.body.text }
        />

        <CardStats
          likes={ displayPost.stats.likes }
          dislikes={ displayPost.stats.dislikes }
          comments={ displayPost.stats.comments }
        />

        {
          showCopyLinkButton
            ? (
              <CardButtons
                handleLikeClick={ handler.like }
                handleDislikeClick={ handler.dislike }
                showCopyLinkButton={ true }
                isLiked={ isLiked }
                isDisliked={ isDisliked }
                isLikePressed={ isLikePressed }
                isDislikePressed={ isDislikePressed }
              />
            )
            : (
              <CardButtons
                handleLikeClick={handler.like}
                handleDislikeClick={handler.dislike}
                handleDetailsClick={handler.details}
                isLiked={ isLiked }
                isDisliked={ isDisliked }
                isLikePressed={ isLikePressed }
                isDislikePressed={ isDislikePressed }
              />
            )
        }
        
      </div>
    </li>
  );
}
